import {
   DeviceType,
   LineHr,
   StyledModal,
   Typography,
   useDeviceType,
   DelayedInlineLoading,
   MetrikaLayoutStack,
} from '@metrika/ui-kit';
import { Button, TextInput } from '@metrika/ui-kit/carbon';
import { ArrowLeft, ArrowRight } from '@metrika/ui-kit/icons/carbon';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { appEnv } from '../../../_env';
import { displayToast } from '../../../_shared/components';
import { qsParse } from '../../../_shared/queryString';
import { useIsLogged } from '../../../user/hooks';
import { useLogin } from '../../hooks/useLogin';
import {
   getAvailableAuthMethods,
   isValidEmail,
   isValidOtpCode,
   sanitizeOtpCode,
   AuthMethod,
   OTP_CODE_LENGTH,
} from '../../utils';
import {
   LoginButtonsHelp,
   LoginButtonsWrapper,
   LoginLogosWrapper,
   LoginTerms,
   LoginWrapper,
   ModalDialogText,
   OtpInputWrapper,
} from './styles';

// The guidelines for Google Login point to using the Google icon on the left side
// https://developers.google.com/identity/branding-guidelines
export const Login = () => {
   const { isLogged, userLoading } = useIsLogged();

   const { search } = useLocation();

   // Default destination on post-login when no return_to is provided. A
   // return_to on the legacy risk app is kept as is (the rewrite to the
   // risk-next overview was dropped: it broke deep links into risk.metrika.co).
   // Always the real risk-next domain: Kratos rejects any return_to that is not
   // whitelisted, so a local origin fails with "Requested return_to URL is not allowed".
   const DEFAULT_RETURN_TO = `${appEnv.appDomains.riskNextReturnTo}/risk/overview`;
   const rawReturnTo = qsParse(search).return_to as string | undefined;
   const return_to: string = (() => {
      if (!rawReturnTo) return DEFAULT_RETURN_TO;
      try {
         const candidate = new URL(rawReturnTo);
         // On localhost risk-next runs on :5173, which Kratos does not allow:
         // keep the path, swap the origin for the real domain.
         if (candidate.origin === new URL(appEnv.appDomains.riskNext).origin) {
            return `${appEnv.appDomains.riskNextReturnTo}${candidate.pathname}${candidate.search}${candidate.hash}`;
         }
      } catch {
         // Not a parseable URL — keep raw value (could be a relative path)
      }
      return rawReturnTo;
   })();
   const goodbye: string = (qsParse(search).goodbye as string) || '';

   const [goodbyeUser, setGoodbyeUser] = useState<string>(goodbye);

   const { loginLoading, onLoginClick } = useLogin(return_to);

   // Two-step flow state
   const [step, setStep] = useState<'email' | 'auth-methods'>('email');
   const [email, setEmail] = useState('');
   const [availableMethods, setAvailableMethods] = useState<AuthMethod[]>([]);
   const [showOtp, setShowOtp] = useState(false);
   const [otp, setOtp] = useState('');
   const [otpTouched, setOtpTouched] = useState(false);
   const [userLoginLoading, setUserLoginLoading] = useState(false);
   const [codeRequestCountdown, setCodeRequestCountdown] = useState(0);
   const [verifyCountdown, setVerifyCountdown] = useState(0);
   const csrfToken = useRef<string>('');
   const actionUrl = useRef<string>('');
   const inFlight = useRef(false);

   const dt = useDeviceType();

   useEffect(() => {
      if (codeRequestCountdown <= 0 && verifyCountdown <= 0) return;
      const t = setTimeout(() => {
         setCodeRequestCountdown((s) => Math.max(0, s - 1));
         setVerifyCountdown((s) => Math.max(0, s - 1));
      }, 1000);
      return () => clearTimeout(t);
   }, [codeRequestCountdown, verifyCountdown]);

   const parseRetryAfter = (header: string | null): number => {
      const n = Number.parseInt(header || '', 10);
      if (Number.isFinite(n) && n > 0) return n;
      const date = header ? Date.parse(header) : Number.NaN;
      if (Number.isFinite(date)) {
         const seconds = Math.ceil((date - Date.now()) / 1000);
         if (seconds > 0) return seconds;
      }
      return 60;
   };

   const buildLocalActionUrl = (): string | null => {
      const url = actionUrl.current;
      if (!url) return null;
      try {
         const parsed = new URL(url);
         return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
         return null;
      }
   };

   const resetFlow = () => {
      setStep('email');
      setShowOtp(false);
      setOtp('');
      setOtpTouched(false);
      setAvailableMethods([]);
      setCodeRequestCountdown(0);
      setVerifyCountdown(0);
      csrfToken.current = '';
      actionUrl.current = '';
   };

   const handleEmailSubmit = () => {
      if (!isValidEmail(email)) {
         displayToast('error', 'Invalid Email', 'Please enter a valid email address');
         return;
      }

      const { methods } = getAvailableAuthMethods(email);
      setAvailableMethods(methods);
      setStep('auth-methods');
   };

   const sendCodeRequest = async (resend: boolean): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      try {
         setUserLoginLoading(true);

         if (!resend) {
            const data = await fetch(
               // return_to is already decoded here; re-encode it (it may carry a
               // nested query string, e.g. the OAuth authorize URL) so Kratos keeps
               // the whole value instead of truncating at the first '&'.
               `${appEnv.api.loginBrowser}?refresh=true${return_to ? `&return_to=${encodeURIComponent(return_to)}` : ''}`,
               {
                  credentials: 'include',
                  headers: {
                     Accept: 'application/json',
                  },
               },
            ).then((d) => d.json());

            const action = data?.ui?.action;
            const csrf_token =
               data?.ui?.nodes?.find((n: { attributes: { name: string } }) => n?.attributes?.name === 'csrf_token')
                  ?.attributes?.value || '';

            if (!action) {
               throw new Error('Login initialization error!');
            }

            csrfToken.current = csrf_token;
            actionUrl.current = action;
         }

         const body: Record<string, string> = {
            csrf_token: csrfToken.current,
            identifier: email,
            method: 'code',
         };
         if (resend) body.resend = 'true';

         const localUrl = buildLocalActionUrl();
         if (!localUrl) {
            throw new Error('Login initialization error!');
         }

         const response = await fetch(localUrl, {
            credentials: 'include',
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
         });

         if (response.status === 410) {
            const expired = await response.json().catch(() => null);
            displayToast(
               'error',
               'Session Expired',
               expired?.statusMessage || 'Your login session has expired. Please start again.',
            );
            resetFlow();
            return false;
         }

         if (response.status === 429) {
            const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
            const data = await response.json().catch(() => null);
            displayToast(
               'error',
               'Too Many Requests',
               data?.statusMessage || `Please wait ${retryAfter} seconds before requesting another code.`,
            );
            setCodeRequestCountdown((prev) => Math.max(prev, retryAfter));
            // Show OTP screen on resend rate-limit (the email was sent earlier);
            // on the very first send return false to keep the user on the methods step.
            return resend;
         }

         const dataPost = await response.json();

         if (
            dataPost?.state === 'sent_email' &&
            !dataPost?.ui?.messages?.find((m: { type: string }) => m.type === 'error')
         ) {
            const info = dataPost?.ui?.messages?.find((m: { type: string }) => m.type === 'info');
            if (info) {
               displayToast('info', resend ? 'Code Resent!' : 'Email Sent!', info.text);
            }
            setCodeRequestCountdown(60);
            setVerifyCountdown(0);
            return true;
         } else {
            const error = dataPost?.ui?.messages?.find((m: { type: string }) => m.type === 'error');
            throw new Error(error?.text || 'Error while sending email. Please try again later.');
         }
      } catch (e) {
         displayToast('error', resend ? 'Resend Failed!' : 'Login Failed!', (e as Error)?.message || 'Unknown Error');
         return false;
      } finally {
         setUserLoginLoading(false);
         inFlight.current = false;
      }
   };

   const handleSendOtp = async () => {
      if (codeRequestCountdown > 0 || userLoginLoading) return;
      const ok = await sendCodeRequest(false);
      if (ok) setShowOtp(true);
   };

   const handleResendOtp = async () => {
      if (codeRequestCountdown > 0 || userLoginLoading) return;
      await sendCodeRequest(true);
   };

   const handleVerifyOtp = async () => {
      if (verifyCountdown > 0 || userLoginLoading || !isValidOtpCode(otp) || inFlight.current) return;
      inFlight.current = true;
      try {
         setUserLoginLoading(true);

         const localUrl = buildLocalActionUrl();
         if (!localUrl) {
            throw new Error('Your login session has expired. Please start again.');
         }

         const response = await fetch(localUrl, {
            credentials: 'include',
            method: 'POST',
            redirect: 'manual',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               csrf_token: csrfToken.current,
               identifier: email,
               method: 'code',
               code: otp,
            }),
         });

         // auth-util aborts a failed login with a 303 to the login-error page:
         // with redirect:'manual' it surfaces here as an opaqueredirect instead
         // of an unreadable (or cross-origin failing) follow-up fetch.
         if (response.type === 'opaqueredirect') {
            throw new Error('Login failed due to an internal error. Please try again or contact support.');
         }

         if (response.status === 410) {
            const expired = await response.json().catch(() => null);
            displayToast(
               'error',
               'Session Expired',
               expired?.statusMessage || 'Your login session has expired. Please start again.',
            );
            resetFlow();
            return;
         }

         if (response.status === 429) {
            const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
            const data = await response.json().catch(() => null);
            displayToast(
               'error',
               'Too Many Requests',
               data?.statusMessage || `Please wait ${retryAfter} seconds before trying again.`,
            );
            setVerifyCountdown((prev) => Math.max(prev, retryAfter));
            return;
         }

         const dataPost = await response.json();

         if (dataPost?.session?.active) {
            const returnTo = dataPost?.continue_with?.find(
               (c: { action: string }) => c.action === 'redirect_browser_to',
            )?.redirect_browser_to;
            if (returnTo) {
               window.location.href = returnTo;
               return;
            } else {
               throw new Error('Login redirect error!');
            }
         } else {
            const error = dataPost?.ui?.messages?.find((m: { type: string }) => m.type === 'error');
            throw new Error(error?.text || 'Invalid OTP code. Please try again.');
         }
      } catch (e) {
         displayToast('error', 'Login Failed!', (e as Error)?.message || 'Unknown Error');
      } finally {
         setUserLoginLoading(false);
         inFlight.current = false;
      }
   };

   const formatCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

   if (isLogged) {
      console.log('Redirecting to home....');
      return <Redirect to="/" />;
   }

   return (
      <>
         <Helmet>
            <title>Login</title>
         </Helmet>
         <LoginWrapper>
            <div className="login-left">
               <LoginLogosWrapper>
                  <img src={'/assets/images/metrika_vertical_no_bg.png'} alt="Metrika Logo" className="logo-metrika" />
               </LoginLogosWrapper>
            </div>
            <div className="login-right">
               <LoginButtonsWrapper>
                  <Typography.H1>Sign in</Typography.H1>
                  <LineHr />
                  {userLoading && <DelayedInlineLoading />}

                  {/* Step 1: Email input */}
                  {step === 'email' && (
                     <MetrikaLayoutStack>
                        <TextInput
                           disabled={userLoginLoading}
                           id="email"
                           labelText="Email"
                           placeholder="Enter your email address"
                           type="email"
                           required
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           onKeyDown={(e) => {
                              if (e.key === 'Enter' && email.length > 0) {
                                 handleEmailSubmit();
                              }
                           }}
                        />
                        <Button
                           kind="tertiary"
                           size={dt < DeviceType.Medium ? 'sm' : 'md'}
                           renderIcon={ArrowRight}
                           disabled={loginLoading || userLoading || userLoginLoading || email.length === 0}
                           onClick={handleEmailSubmit}
                        >
                           Continue
                        </Button>
                     </MetrikaLayoutStack>
                  )}

                  {/* Step 2: Auth method selection */}
                  {step === 'auth-methods' && (
                     <MetrikaLayoutStack>
                        <Typography variant={'bodyLong01'} color={'text02'}>
                           {email}
                        </Typography>

                        {!showOtp ? (
                           <>
                              {availableMethods.includes('google') && (
                                 <Button
                                    kind="tertiary"
                                    size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                    renderIcon={() => <span className={'cds--btn__icon google'}></span>}
                                    onClick={() => onLoginClick('google')}
                                    disabled={loginLoading || userLoading}
                                 >
                                    Sign in with Google
                                 </Button>
                              )}
                              {availableMethods.includes('microsoft') && (
                                 <Button
                                    kind="tertiary"
                                    size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                    renderIcon={() => <span className={'cds--btn__icon microsoft'}></span>}
                                    onClick={() => onLoginClick('microsoft')}
                                    disabled={loginLoading || userLoading}
                                 >
                                    Sign in with Microsoft
                                 </Button>
                              )}
                              {availableMethods.includes('scb') && (
                                 <Button
                                    kind="tertiary"
                                    size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                    renderIcon={() => <span className={'cds--btn__icon scb'}></span>}
                                    onClick={() => onLoginClick('scb')}
                                    disabled={loginLoading || userLoading}
                                 >
                                    Sign in with SCB
                                 </Button>
                              )}
                              {availableMethods.includes('otp') && (
                                 <Button
                                    kind="tertiary"
                                    size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                    disabled={
                                       loginLoading || userLoading || userLoginLoading || codeRequestCountdown > 0
                                    }
                                    onClick={handleSendOtp}
                                 >
                                    {codeRequestCountdown > 0
                                       ? `Send OTP in email (${formatCountdown(codeRequestCountdown)})`
                                       : 'Send OTP in email'}
                                 </Button>
                              )}
                              <Button
                                 kind="ghost"
                                 size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                 renderIcon={ArrowLeft}
                                 disabled={loginLoading || userLoading || userLoginLoading}
                                 onClick={resetFlow}
                              >
                                 Change email
                              </Button>
                           </>
                        ) : (
                           <>
                              <OtpInputWrapper>
                                 <TextInput
                                    id="otp"
                                    labelText="OTP Code"
                                    placeholder={'0'.repeat(OTP_CODE_LENGTH)}
                                    type="text"
                                    // Numeric keypad on mobile and native OTP autofill; the field is
                                    // plain text so the code stays visible at all times.
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={OTP_CODE_LENGTH}
                                    autoFocus
                                    required
                                    value={otp}
                                    invalid={otpTouched && otp.length > 0 && !isValidOtpCode(otp)}
                                    invalidText={`The code is ${OTP_CODE_LENGTH} digits`}
                                    onChange={(e) => setOtp(sanitizeOtpCode(e.target.value))}
                                    onBlur={() => setOtpTouched(true)}
                                    onKeyDown={(e) => {
                                       if (e.key !== 'Enter') return;
                                       setOtpTouched(true);
                                       if (isValidOtpCode(otp) && verifyCountdown === 0 && !userLoginLoading) {
                                          handleVerifyOtp();
                                       }
                                    }}
                                 />
                              </OtpInputWrapper>

                              <Button
                                 kind="tertiary"
                                 size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                 renderIcon={ArrowRight}
                                 disabled={
                                    loginLoading ||
                                    userLoading ||
                                    userLoginLoading ||
                                    !isValidOtpCode(otp) ||
                                    verifyCountdown > 0
                                 }
                                 onClick={handleVerifyOtp}
                              >
                                 {verifyCountdown > 0
                                    ? `Verify OTP (${formatCountdown(verifyCountdown)})`
                                    : 'Verify OTP'}
                              </Button>

                              <Button
                                 kind="ghost"
                                 size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                 disabled={loginLoading || userLoading || userLoginLoading || codeRequestCountdown > 0}
                                 onClick={handleResendOtp}
                              >
                                 {codeRequestCountdown > 0
                                    ? `Resend Code (${formatCountdown(codeRequestCountdown)})`
                                    : 'Resend Code'}
                              </Button>

                              <Button
                                 kind="ghost"
                                 size={dt < DeviceType.Medium ? 'sm' : 'md'}
                                 renderIcon={ArrowLeft}
                                 disabled={loginLoading || userLoading || userLoginLoading}
                                 onClick={() => {
                                    setShowOtp(false);
                                    setOtp('');
                                    setOtpTouched(false);
                                 }}
                              >
                                 Choose another method
                              </Button>
                           </>
                        )}
                     </MetrikaLayoutStack>
                  )}
                  <LineHr />
                  <LoginButtonsHelp>
                     Need Help?{' '}
                     <a className="cds--link" href="mailto:support@metrika.co">
                        Contact our support team
                     </a>
                     .
                  </LoginButtonsHelp>
               </LoginButtonsWrapper>
            </div>
            <LoginTerms>
               <p>
                  By signing in you agree to our&nbsp;
                  <Link to="/terms" className="cds--link">
                     Terms and Conditions
                  </Link>
                  .
               </p>
            </LoginTerms>
            {goodbyeUser && (
               <StyledModal
                  modalLabel="Goodbye!"
                  modalHeading="Your account has been deleted."
                  size="sm"
                  passiveModal={true}
                  open
                  onRequestClose={() => setGoodbyeUser('')}
               >
                  <p>Hi {goodbyeUser},</p>
                  <ModalDialogText>
                     The Metrika account associated with the email {goodbyeUser} has been permanently deleted. You may
                     now close the browser window.
                  </ModalDialogText>
                  <ModalDialogText>
                     If you wish to create a new account, you can sign-up with Google or GitHub.
                  </ModalDialogText>
               </StyledModal>
            )}
         </LoginWrapper>
      </>
   );
};
